import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';
import LibraryFreeNote from '../../components/LibraryFreeNote';
import {
  Search, ExternalLink, ShieldAlert, Loader2, ClipboardList, BookMarked,
  CheckCircle2, Info, Lock, Filter, X,
} from 'lucide-react';

const LOGO_URL = '/inasw-logo-2026.jpg';

const BADGE = {
  official_form: { cls: 'bg-green-100 text-green-800 border-green-300', dot: 'bg-green-500', label: 'Official Form Available', Icon: CheckCircle2, btn: 'Access Official Form' },
  official_page: { cls: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500', label: 'Official Access Page', Icon: Info, btn: 'Visit Official Source' },
  listing_only: { cls: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500', label: 'License or Purchase Required', Icon: Lock, btn: 'Get Form from Publisher' },
};

const AccessBadge = ({ label }) => {
  const b = BADGE[label] || BADGE.listing_only;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${b.cls}`} data-testid={`access-badge-${label}`}>
      <span className={`w-2 h-2 rounded-full ${b.dot}`} /> {b.label}
    </span>
  );
};

const Chip = ({ children }) => (
  <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{children}</span>
);

const InstrumentCard = ({ inst, saved, onSaved }) => {
  const b = BADGE[inst.access_label] || BADGE.listing_only;
  const hasUrl = !!inst.official_url;
  return (
    <div className="bg-white rounded-xl border p-5 flex flex-col" data-testid={`instrument-card-${inst.instrument_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-serif text-lg font-bold text-primary leading-snug">{inst.name}</h3>
          {inst.abbr && <p className="text-sm font-semibold text-secondary">{inst.abbr}</p>}
          <p className="text-xs text-gray-500 mt-0.5">{inst.subcategory}</p>
        </div>
      </div>
      <div className="mt-2"><AccessBadge label={inst.access_label} /></div>
      {inst.purpose && <p className="text-sm text-gray-600 mt-3">{inst.purpose}</p>}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {inst.classification && <Chip>{inst.classification}</Chip>}
        {(inst.age_group || []).slice(0, 3).map((a) => <Chip key={a}>{a}</Chip>)}
        {inst.administration && <Chip>{inst.administration}</Chip>}
      </div>
      {inst.warning && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2 text-xs text-amber-800">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 text-amber-600" /><span>{inst.warning}</span>
        </div>
      )}
      {inst.note && <p className="text-xs text-gray-500 mt-2 italic">{inst.note}</p>}
      <div className="text-xs text-gray-500 mt-3 border-t pt-2 space-y-0.5">
        {inst.official_source && <p><strong className="text-gray-600">Source:</strong> {inst.official_source}</p>}
        {inst.copyright_status && <p><strong className="text-gray-600">Access:</strong> {inst.copyright_status}</p>}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {hasUrl ? (
          <a href={inst.official_url} target="_blank" rel="noreferrer">
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" data-testid={`access-btn-${inst.instrument_id}`}>
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> {b.btn}
            </Button>
          </a>
        ) : (
          <Button size="sm" variant="outline" disabled title="Access via the publisher or issuing authority">
            <Lock className="w-3.5 h-3.5 mr-1" /> Publisher access required
          </Button>
        )}
        <SaveToLibraryButton
          saved={saved}
          onSaved={onSaved}
          item={{ item_type: 'behavioral_health', ref_id: inst.instrument_id, title: inst.name,
            subtitle: inst.abbr, category: inst.subcategory, url: inst.official_url }}
        />
      </div>
    </div>
  );
};

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-xs font-semibold text-white/80 mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white text-gray-900 rounded-md px-3 h-10 text-sm border" data-testid={testid}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const BehavioralHealthDirectory = () => {
  const { isAuthenticated } = useAuth();
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ category: '', subcategory: '', access_label: '', age_group: '', administration: '', classification: '' });
  const [instruments, setInstruments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savedRefs, setSavedRefs] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [freeOnly, setFreeOnly] = useState(false);

  useEffect(() => {
    document.title = 'National Social Work Behavioral Health Forms & Assessments Directory | I Need A Social Worker';
    axios.get(`${API_URL}/behavioral-health/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      if (freeOnly) p.set('free_only', '1');
      p.set('limit', '200');
      const r = await axios.get(`${API_URL}/behavioral-health?${p.toString()}`);
      setInstruments(r.data.instruments || []); setTotal(r.data.total || 0);
    } catch (e) { setInstruments([]); } finally { setLoading(false); }
  }, [q, filters, freeOnly]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API_URL}/library/saved-refs?item_type=behavioral_health`).then((r) => setSavedRefs(r.data.refs || [])).catch(() => {});
  }, [isAuthenticated]);

  const setF = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));
  const onSaved = (refId) => setSavedRefs((prev) => prev.includes(refId) ? prev : [...prev, refId]);
  const clearFilters = () => { setFilters({ category: '', subcategory: '', access_label: '', age_group: '', administration: '', classification: '' }); setQ(''); setFreeOnly(false); };
  const subcatsForCat = meta?.categories?.find((c) => c.slug === filters.category)?.subcategories || [];

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      {/* Hero */}
      <section className="bg-primary pt-24 pb-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img src={LOGO_URL} alt="INASW" className="w-16 h-16 rounded-full object-cover ring-2 ring-secondary mb-4" />
          <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Verified Professional Index</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mt-1">National Social Work <span className="text-secondary">Behavioral Health</span> Forms &amp; Assessments Directory<sup className="text-secondary text-lg align-super">™</sup></h1>
          <p className="mt-3 text-white/85 max-w-3xl">{meta?.intro}</p>
        </div>
      </section>

      {/* Access legend */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="mb-3"><LibraryFreeNote variant="banner" /></div>
        <div className="bg-white rounded-2xl border p-4 shadow-lg grid sm:grid-cols-3 gap-3" data-testid="access-legend">
          {Object.entries(BADGE).map(([k, b]) => (
            <div key={k} className={`rounded-lg border p-3 ${b.cls}`}>
              <div className="flex items-center gap-2 font-semibold text-sm"><b.Icon className="w-4 h-4" /> {b.label}</div>
              <p className="text-[11px] mt-1 opacity-80">
                {k === 'official_form' && 'A downloadable/official form is publicly available.'}
                {k === 'official_page' && 'Access the instrument via its official publisher page.'}
                {k === 'listing_only' && 'Obtain directly from the official publisher or copyright holder (license, training, or purchase may apply).'}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Search */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-primary/95 rounded-2xl p-5 shadow-lg" data-testid="bh-search">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchList()}
                placeholder="Search by name or abbreviation (e.g. PHQ-9, GAD-7, C-SSRS, AUDIT)…"
                className="w-full bg-white text-gray-900 rounded-md pl-9 pr-3 h-11 text-sm border" data-testid="bh-search-input" />
            </div>
            <Button onClick={fetchList} className="bg-secondary hover:bg-secondary-hover text-white h-11 px-5" data-testid="bh-search-btn"><Search className="w-4 h-4 mr-1" /> Search</Button>
            <Button onClick={() => setShowFilters((s) => !s)} variant="outline" className="h-11 bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="bh-toggle-filters"><Filter className="w-4 h-4 mr-1" /> Filters</Button>
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4" data-testid="bh-filters">
              <SelectField label="Category" value={filters.category} onChange={(v) => { setF('category', v); setF('subcategory', ''); }} testid="bh-filter-category"
                options={[{ value: '', label: 'All categories' }, ...(meta?.categories || []).map((c) => ({ value: c.slug, label: c.label }))]} />
              <SelectField label="Condition / subcategory" value={filters.subcategory} onChange={(v) => setF('subcategory', v)} testid="bh-filter-subcategory"
                options={[{ value: '', label: 'All' }, ...subcatsForCat.map((s) => ({ value: s, label: s }))]} />
              <SelectField label="Access type" value={filters.access_label} onChange={(v) => setF('access_label', v)} testid="bh-filter-access"
                options={[{ value: '', label: 'Any access' }, { value: 'official_form', label: 'Official Form Available' }, { value: 'official_page', label: 'Official Access Page' }, { value: 'listing_only', label: 'Listing Only' }]} />
              <SelectField label="Age group" value={filters.age_group} onChange={(v) => setF('age_group', v)} testid="bh-filter-age"
                options={[{ value: '', label: 'Any age' }, ...(meta?.age_groups || []).map((a) => ({ value: a, label: a }))]} />
              <SelectField label="Administration" value={filters.administration} onChange={(v) => setF('administration', v)} testid="bh-filter-admin"
                options={[{ value: '', label: 'Any' }, ...(meta?.administration || []).map((a) => ({ value: a, label: a }))]} />
              <SelectField label="Type" value={filters.classification} onChange={(v) => setF('classification', v)} testid="bh-filter-classification"
                options={[{ value: '', label: 'Any type' }, ...(meta?.classifications || []).map((c) => ({ value: c, label: c }))]} />
            </div>
          )}
        </div>
      </section>

      {/* Category quick-links */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex flex-wrap gap-2 items-center">
          <button onClick={clearFilters} className={`px-3 py-1.5 rounded-full text-sm border ${!filters.category ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700'}`} data-testid="bh-cat-all">All</button>
          {(meta?.categories || []).map((c) => (
            <button key={c.slug} onClick={() => { setF('category', c.slug); setF('subcategory', ''); }}
              className={`px-3 py-1.5 rounded-full text-sm border ${filters.category === c.slug ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 hover:border-secondary'}`}
              data-testid={`bh-cat-${c.slug}`}>{c.label}</button>
          ))}
          <button onClick={() => setFreeOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-sm border inline-flex items-center gap-1.5 ml-auto ${freeOnly ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-300 hover:border-green-500'}`}
            data-testid="bh-free-only-toggle" aria-pressed={freeOnly}>
            <CheckCircle2 className="w-4 h-4" /> Free forms only
          </button>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl font-bold text-primary">{total} {total === 1 ? 'instrument' : 'instruments'}</h2>
          <div className="flex items-center gap-3">
            {(q || Object.values(filters).some(Boolean)) && <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-primary flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>}
            {isAuthenticated && <Link to="/my-library?tab=behavioral_health" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="bh-my-library-link"><BookMarked className="w-4 h-4" /> My Library</Link>}
          </div>
        </div>
        {loading ? (
          <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : instruments.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600 font-medium">No instruments match your search.</p>
            <p className="text-gray-400 text-sm mt-1">Try a broader term or clear the filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {instruments.map((inst) => (
              <InstrumentCard key={inst.instrument_id} inst={inst} saved={savedRefs.includes(inst.instrument_id)} onSaved={onSaved} />
            ))}
          </div>
        )}
      </section>

      {/* Notices */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 text-sm text-blue-800" data-testid="bh-index-notice">
          <Info className="w-5 h-5 flex-shrink-0 text-blue-600" /><p>{meta?.index_notice}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-800" data-testid="bh-legal-notice">
          <ShieldAlert className="w-5 h-5 flex-shrink-0 text-amber-600" /><p>{meta?.legal_notice}</p>
        </div>
      </section>
    </div>
  );
};

export default BehavioralHealthDirectory;
